import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
// Import from the service layer, not from a UI component file.
// MemberVaccination.jsx re-exports this for backward compatibility, but
// direct service imports are the correct pattern going forward.
import { createVaccinationRecord } from "./VaccinationModule/services/vaccinationApiService";
import { decryptOpenSSLAES } from "./uwin-decrypt";
import { getBeneficiaryAgeDisplay } from "../../../helpers/common-uwinhelper";

const VaccinationReviewCard = ({ 
  toggleDrawer, 
  beneficiaryData, 
  vaccinationData, 
  onBack, 
  onVaccinateSuccess,
  onReject 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Static/default data for review (will be replaced with actual data)
  console.log(beneficiaryData,"beneficiaryData")
  console.log(vaccinationData,"vaccinationData")
  
  const isPrevious = vaccinationData?.vaccinationType === 'previous';

  const formatDateToDDMMYYYY = (dateValue) => {
    if (!dateValue) return "N/A";

    const dateObj = new Date(dateValue);
    return `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
  };

  const extractedVaccinations = isPrevious
    ? (vaccinationData?.selectedVaccines || []).flatMap((vaccine) =>
        (vaccine.availableDoses || [])
          .filter((dose) => dose.selected)
          .map((dose) => ({
            vaccineName: vaccine.name || "N/A",
            dose: dose.doseName || "N/A",
            dateOfVaccination: formatDateToDDMMYYYY(dose.administeredDate),
          }))
      )
    : (vaccinationData?.selectedVaccines || []).map((vaccine) => ({
        vaccineName: vaccine.name || "N/A",
        dose: vaccine.selectedDose || "N/A",
        dateOfVaccination: formatDateToDDMMYYYY(new Date()),
      }));

  const reviewData = {
    memberDetails: {
      name: beneficiaryData?.ben_name || "N/A",
      age: getBeneficiaryAgeDisplay(beneficiaryData),
      gender: beneficiaryData?.beneficiary_gender || "N/A",
      mobile: decryptOpenSSLAES(beneficiaryData?.ben_mobile_number) || "N/A", // This should come from decrypted data
    },
    vaccinationDetailsList: extractedVaccinations
  };

  // Handle vaccinate member
  const handleVaccinateMember = async () => {
    try {
      setIsSubmitting(true);
      
      console.log('Review Card - Starting vaccination with data:', vaccinationData);
      
      // Validate that we have vaccination data
      if (!vaccinationData) {
        toast.error("No vaccination data available. Please go back and fill the form.");
        return;
      }

      // Determine vaccination type from the form data
      const vaccinationType = vaccinationData.vaccinationType || 'current';
      
      // Call the centralized API service
      const result = await createVaccinationRecord(
        vaccinationData,           // Form data from Step 1
        vaccinationType,           // 'current' or 'previous'  
        null,                      // No rejection data for success
        vaccinationData.beneficiaryData || beneficiaryData, // Beneficiary info
        vaccinationData.villages || [],     // Village data for API
        vaccinationData.healthVillages || [], // Health village data
        vaccinationData.wards || [],        // Ward data
        vaccinationData.healthWards || []    // Health ward data for name lookup
      );

      console.log('Vaccination API result:', result);

      if (result.success) {
        toast.success(result.message || "Member vaccinated successfully!");
        
        // Prepare success data with API response
        const successData = {
          ...reviewData,
          apiResult: result.data,
          vaccinationId: result.data?.vaccination_id || result.data?.id,
          vaccinationType,
          apiPayload: result.payload,
          timestamp: new Date().toISOString()
        };

        // Navigate to success screen with enriched data
        if (onVaccinateSuccess) {
          onVaccinateSuccess(successData);
        }
      } else {
        toast.error(result.message || "Failed to vaccinate member");
      }
      
    } catch (error) {
      console.error("Error vaccinating member:", error);
      
      // Handle specific error messages
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error("Failed to vaccinate member. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reject member - directly call onReject 
  const handleRejectMember = () => {
    if (onReject) {
      onReject();
    } else {
      // Default: close drawer
      toggleDrawer(false)();
    }
  };

  return (
    <Box sx={{ width: 490, height: "100vh", bgcolor: "white" }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Back button */}
            {onBack && (
              <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="h6" fontWeight="bold">
              Member Vaccination
            </Typography>
          </Box>
          
          <IconButton onClick={toggleDrawer(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ================= REVIEW CONTENT ================= */}
      <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
        
        {/* Member Details Card - Simple, No Accordion */}
        <Card sx={{ 
          mb: 2, 
          borderRadius: 2, 
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
        }}>
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 2
              }}
            >
              Member Details
            </Typography>
            
            {/* Member Name and Basic Info */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: "28px",
                  mb: 0.5
                }}
              >
                {reviewData.memberDetails.name}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#6b7280",
                  lineHeight: "20px"
                }}
              >
                {reviewData.memberDetails.age} • {reviewData.memberDetails.gender}
              </Typography>
            </Box>

            {/* Contact Information */}
            <Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#374151",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  mb: 0.5
                }}
              >
                Mobile Number
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1f2937",
                  lineHeight: "20px"
                }}
              >
                {reviewData.memberDetails.mobile}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Vaccination Details Accordion - Keep as is but with improved styling */}
        <Card sx={{ 
          mb: 3, 
          borderRadius: 2, 
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
        }}>
          <Accordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#6b7280" }} />}
              sx={{
                backgroundColor: "#f9fafb",
                minHeight: "auto",
                borderRadius: "8px 8px 0 0",
                "& .MuiAccordionSummary-content": {
                  margin: "16px 0",
                  "&.Mui-expanded": { margin: "16px 0" }
                }
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
               Review Vaccination Details
              </Typography>
            </AccordionSummary>
            
            <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
            {reviewData.vaccinationDetailsList.map((details, index) => (
            <Box key={index} sx={{ mb: index !== reviewData.vaccinationDetailsList.length - 1 ? 3 : 0 }}>
              <Grid container spacing={3}>
                {/* Vaccine Name */}
                <Grid item xs={12}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block", 
                      mb: 0.5 
                    }}
                  >
                    Vaccine Name
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#111827",
                      lineHeight: "24px"
                    }}
                  >
                    {details.vaccineName}
                  </Typography>
                </Grid>

                {/* Dose */}
                <Grid item xs={6}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block", 
                      mb: 0.5 
                    }}
                  >
                    Dose
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#1f2937",
                      lineHeight: "20px"
                    }}
                  >
                    {details.dose}
                  </Typography>
                </Grid>

                {/* Date of Vaccination */}
                <Grid item xs={6}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block", 
                      mb: 0.5 
                    }}
                  >
                    Date of Vaccination
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#1f2937",
                      lineHeight: "20px"
                    }}
                  >
                    {details.dateOfVaccination}
                  </Typography>
                </Grid>
              </Grid>
                
                {/* Add a divider between multiple vaccines */}
                {index !== reviewData.vaccinationDetailsList.length - 1 && (
                <Box sx={{ borderBottom: "1px dashed #e0e0e0", mt: 3 }} />
                )}
            </Box>
            ))}
            </AccordionDetails>
          </Accordion>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: "auto", pt: 2 }}>
          {/* Vaccinate Member Button */}
          <Button
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            onClick={handleVaccinateMember}
            sx={{
              py: 1.5,
              mb: 2,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : (
              "Vaccinate Member"
            )}
          </Button>

          {/* Reject Member — Current Vaccination only */}
        {!isPrevious && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRejectMember}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                borderColor: "error.dark",
                backgroundColor: "error.light",
              }
            }}
          >
            Reject Member
          </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VaccinationReviewCard;